import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import * as amplitude from '@amplitude/analytics-browser';

import { LinkPage, handleLeaveLink } from '../components/ButtonExitLink';
import { errorPageStyles } from '../components/ComponentStyles';
import { useAppState } from '../contexts/AppStateProvider';
// Use new hooks instead of legacy context
import { useJWT } from '../contexts/JWTProvider';
import { ServiceThemeOptions } from '../themes';
import {
  getAnalyticsContext,
  getServiceDisplayName,
} from '../utils/jwtHelpers';

export default function NotFoundPage() {
  const theme = useTheme() as ServiceThemeOptions;
  const navigate = useNavigate();
  const location = useLocation();

  // Use new centralized hooks
  const jwt = useJWT();
  const { serviceName } = useAppState();

  // Get service display name
  const serviceDisplayName = getServiceDisplayName(jwt, serviceName);

  // Extract theme values safely
  const warningColor = (theme.palette?.warning as any)?.main || '#FD9726';
  const paperBgColor = theme.palette?.background?.paper || '#FFFFFF';

  // Track page view with enhanced JWT context
  useEffect(() => {
    const analyticsContext = getAnalyticsContext(jwt, serviceName);

    amplitude.track('404 Page View', {
      ...analyticsContext,
      attemptedPath: location.pathname,
      hasValidToken: jwt.isValid,
    });
  }, [serviceName, jwt, location.pathname]);

  // Handle going to home/start
  const handleGoHome = () => {
    const analyticsContext = getAnalyticsContext(jwt, serviceName);

    amplitude.track('User Clicked Return to Onboarding from 404', {
      ...analyticsContext,
      attemptedPath: location.pathname,
    });

    navigate({
      pathname: '/',
    });
  };

  // Handle return to service for users with decodable tokens
  const handleReturnToService = () => {
    const analyticsContext = getAnalyticsContext(jwt, serviceName);

    amplitude.track('User Returned to Service from 404', {
      ...analyticsContext,
      attemptedPath: location.pathname,
      redirectURL: jwt.preferredRedirectURI,
    });

    if (jwt.preferredRedirectURI) {
      handleLeaveLink({
        token: jwt.rawToken || '',
        redirectURIError: jwt.preferredRedirectURI,
        serviceName: serviceDisplayName,
        pageOnExit: LinkPage.Approve,
      });
    } else {
      // Fallback to home if no redirect URI
      handleGoHome();
    }
  };

  return (
    <Box sx={errorPageStyles.mainContainer}>
      <Paper
        elevation={3}
        sx={{
          ...errorPageStyles.paper,
          bgcolor: paperBgColor,
        }}
      >
        {/* Icon Container */}
        <Box sx={errorPageStyles.iconContainer}>
          <HelpOutlineIcon
            sx={{
              fontSize: 94,
              color: warningColor,
            }}
          />
        </Box>

        {/* Title */}
        <Typography
          variant='h4'
          sx={{
            ...errorPageStyles.title(theme as any),
          }}
        >
          Page Not Found
        </Typography>

        {/* Description */}
        <Typography
          variant='body1'
          sx={{
            ...errorPageStyles.caption(theme as any),
          }}
        >
          We couldn't find the page you were looking for.
        </Typography>

        {/* Buttons */}
        <Stack spacing={1.25} sx={errorPageStyles.buttonsContainer}>
          {/* Primary action - Return to onboarding if JWT is valid */}
          {jwt.isValid && jwt.rawToken ? (
            <Button
              variant='contained'
              onClick={handleGoHome}
              sx={errorPageStyles.primaryButton(theme as any)}
            >
              RETURN TO ONBOARDING
            </Button>
          ) : jwt.canRedirectToService ? (
            <Button
              variant='contained'
              onClick={handleReturnToService}
              sx={errorPageStyles.primaryButton(theme as any)}
            >
              RETURN TO {serviceDisplayName}
            </Button>
          ) : (
            <Typography
              variant='body2'
              sx={{
                textAlign: 'center',
                fontStyle: 'italic',
                color: 'text.secondary',
                mt: 2,
                p: 2,
                bgcolor: 'action.hover',
                borderRadius: 1,
              }}
            >
              You can safely close this tab or window.
            </Typography>
          )}

          {/* Secondary action - Return to service if we have a redirect URI */}
          {jwt.isValid && jwt.rawToken && jwt.canRedirectToService && (
            <Button
              variant='text'
              onClick={handleReturnToService}
              sx={errorPageStyles.secondaryButton(theme as any)}
            >
              RETURN TO {serviceDisplayName}
            </Button>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
